import { Http } from './http';
import { AdminComment, Admin, Timer, AdminArea, AdminRole } from '@types';
import { NotificationType } from '@components/Notifications';

export interface UpdateAdminParams {
  reports?: string[];
  reportRange?: string;
  reportType?: string;
}

class AdminHttp extends Http {

  async getAuthCode(params: GetAuthCodeParams): Promise<GetAuthCodeResponse> {
    const res = await this.post<GetAuthCodeResponse>('get-auth-code', params);

    return res.data;
  }

  async checkAuthCode(params: CheckAuthCodeParams): Promise<Admin> {
    const res = await this.post<{admin: Admin}>('check-auth-code', params);

    return res.data.admin;
  }

  async login(params: LoginParams): Promise<Admin> {
    const res = await this.post<{admin: Admin}>('login', params);

    return res.data.admin;
  }

  async updateMe(params: UpdateAdminParams): Promise<Admin> {
    const res = await this.put<{admin: Admin}>('me', params);

    return res.data.admin;
  }

  async me(): Promise<Admin> {
    const res = await this.get<{admin: Admin}>('me');
    const { admin } = res.data;

    if (admin && admin.permissionKeys && admin.permissionKeys['finance-dashboard|ALL']) {
      admin.permissionKeys['finance-balance|ALL'] = true;
    }

    return admin;
  }

  async getAll(): Promise<Admin[]> {
    const res = await this.get<Admin[]>('admin');

    return res.data;
  }

  async logout() {
    return await this.get('logout');
  }

  async getResponsibleManagers(): Promise<Admin[]> {
    const res = await this.get<Admin[]>('admin/managers');

    return res.data;
  }

  async getTaskAdmin(): Promise<Admin[]> {
    const res = await this.get<Admin[]>('admin/task-admin');

    return res.data;
  }

  async getResponsibleOwners(): Promise<Admin[]> {
    const res = await this.get<Admin[]>('admin/owners');

    return res.data;
  }

  async getCollectors(): Promise<Admin[]> {
    const res = await this.get<Admin[]>('admin/collectors');

    return res.data;
  }

  async addComment(params: AddCommentParams): Promise<AdminComment> {
    const res = await this.post<AdminComment>('comment', params);

    return res.data;
  }

  async removeComment(id: number): Promise<AdminComment> {
    const res = await this.delete<AdminComment>(`comment/${id}`);

    return res.data;
  }

  async getAllNotifications(): Promise<NotificationType> {
    const res = await this.post<NotificationType>('notifications', {
      date: new Date(),
    });

    return res.data;
  }

  async readNotifications(entity: NotificationEntityTypes): Promise<any[]> {
    const res = await this.get<any[]>(`notifications/${entity}`);

    return res.data;
  }

  async readNotification(id: number): Promise<boolean> {
    const res = await this.get<boolean>(`notifications/item/${id}`);

    return res.data;
  }

  async clearNotifications(): Promise<any[]> {
    const res = await this.put<any[]>('notifications/clear');

    return res.data;
  }

  async getTaskAdmins(): Promise<Admin[]> {
    const res = await this.get<Admin[]>('admin/task-admin');

    return res.data;
  }

  async getTimer(url: string) {
    const res = await this.get<Timer>(url);

    return res.data;
  }

  async getRoles() {
    const res = await this.get<AdminRole[]>('role');

    return res.data;
  }

  async getAreas() {
    const res = await this.get<AdminArea[]>('area-options');

    return res.data;
  }
}

export const adminHttp = new AdminHttp();

interface LoginParams {
  username: string;
  password: string;
}

interface GetAuthCodeParams {
  username: string;
  password: string;
  viaPhone?: boolean;
}

export interface CheckAuthCodeParams {
  username: string;
  password: string;
  code: string;
  viaPhone?: boolean;
}

interface AddCommentParams {
  id?: number;
  comment: string;
  partnerClass: string;
  partnerId: number;
  showToUser?: boolean;
  showToPartner?: boolean;
}

export enum NotificationEntityTypes {
  USER_ASSIGNED = 'USER_ASSIGNED',
  USER_CONTACT_OWNER_UPDATED = 'USER_CONTACT_OWNER_UPDATED',
  CREDIT_FILES_PROGRESS = 'CREDIT_FILES_PROGRESS',
  CREDIT_STATUS_MODERATION_SIGNED = 'CREDIT_STATUS_MODERATION_SIGNED',
  NEW_CREDIT = 'NEW_CREDIT',
  NEW_CONTACT_US = 'NEW_CONTACT_US',
  CREDIT_STATUS_CX = 'CREDIT_STATUS_CX',
  CREDIT_STATUS_DELAY_CX = 'CREDIT_STATUS_DELAY_CX',
  NEW_RESUME = 'NEW_RESUME',
  NEW_QUOTATION = 'NEW_QUOTATION',
  INVOICE_UPLOADED = 'INVOICE_UPLOADED',
  CALL_OF_THE_DAY = 'CALL_OF_THE_DAY',
  CONTACT_NEXT_CYCLE = 'CONTACT_NEXT_CYCLE',
  CREDIT_CONTACT_NEXT_CYCLE = 'CREDIT_CONTACT_NEXT_CYCLE',
  USER_UPLOADED_DOCUMENTS = 'USER_UPLOADED_DOCUMENTS',
  FINANCE_COST_CREATED = 'FINANCE_COST_CREATED',
  FINANCE_WITHDRAWAL_CREATED = 'FINANCE_WITHDRAWAL_CREATED',
  FINANCE_DEPOSIT_CREATED = 'FINANCE_DEPOSIT_CREATED',
  TICKET_CREATED = 'TICKET_CREATED',
  NEW_PARTNER_REGISTERED = 'NEW_PARTNER_REGISTERED',

  // Credit Statuses
  MODERATION = 'moderation',
  PRE_SCREENING = 'pre_screening',
  DOC_COLLECTION = 'doc_collection',
  KYC_SCREENING = 'kyc_screening',
  POST_SCREENING = 'post_screening',
  COMPLETED_FILE = 'completed_file',
  BANK_REVISION = 'bank_revision',
  APPROVED = 'approved',
  IN_NEGOTIATIONS = 'in_negotiations',
  CONTRACT_IN_PROCESS = 'contract_in_process',
  SIGNED = 'signed',
  REPAID = 'repaid',
  RESTRUCTURE = 'restructure',
  DELAY = 'delay',
  DENIED = 'denied',
}

export interface GetAuthCodeResponse {
  expiresAt: string;
  email: string;
  phone: string;
}
